import { Helper } from "../helpers/helper";
import { PopupCloseCallback } from "./popup.models";

declare global {
    interface Window {
        FindURLParameter: (key: string) => string;
        Page_Validators: HTMLElement[];
        ValidatorValidate: (validator: HTMLElement) => void;
        ValidatorOnChange_Original: (target: any) => void;
        ValidatorOnLoad: () => void;
        ValidatorEnable: (val: HTMLElement, enable: boolean) => void;
        Page_ClientValidate: (validationGroup?: string) => boolean;
        GetRadWindow: () => Telerik.Web.UI.RadWindow;
        GetRadWindowManager: () => Telerik.Web.UI.RadWindowManager;
        ShowDialog: (url: string, args: any, width: string, height: string, title: string, iconUrl: string, templateType: string, callBackFunction: () => void, windowName: string, closeWindowOnCommit: boolean, preserveStatefulBusinessContainer: boolean, dialogCloseFunction: PopupCloseCallback) => void;
        showStatusMessage: (messageContent: string, messageType: "Information" | "Success" | "Warning" | "Error" | "Validation" | "Neutral") => void;
        jQuery: JQueryStatic;
    }

    interface HTMLElement<T = any> {
        validationGroup: string;
        control: T;
    }

    /**
    * Returns the specified Component object. This member is static and can be invoked without creating an instance of the class.
    * @see {@link http://msdn.microsoft.com/en-us/library/bb397441(v=vs.100).aspx}
    * @param id A string that contains the ID of the component to find.
    * @param parent (Optional) The component or element that contains the component to find.
    * @return A Component object that contains the component requested by ID, if found; otherwise, null.
    */
    function $find<T>(id: string, parent?: Sys.Component): T;
    function $find<T>(id: string, parent?: HTMLElement): T;
}

export enum ImisMessageType {
    SUCCESS = "AsiSuccess",
    INFORMATION = "AsiInformation",
    WARNING = "AsiWarning",
    ERROR = "AsiError"
}

export type PayloadFieldToTypeMapping = {
    [key: string]: ImisPayloadFieldType 
};

export enum ImisPayloadFieldType {
    BINARY = "binary"
}

export class ImisClientContext {
    appTimeZoneOffset: number;
    baseUrl: string;
    cookieConsent: number;
    isAnonymous: boolean;
    loggedInPartyId: string;
    selectedPartyId: string;
    virtualDir: string;
    websiteRoot: string;
}

export type ImisAuthenticationGrantType = "password" | "refresh_token";

export class ImisAuthentication {
    username: string;
    password: string;
    grant_type: ImisAuthenticationGrantType;
    refresh_token?: string;

    constructor(username: string, password: string, refreshToken?: string) {
        this.username = username;
        this.password = password;
        this.refresh_token = refreshToken;
        this.grant_type = Helper.isStringNullOrEmpty(this.refresh_token) ? "password" : "refresh_token";
    }
}

export type ImisCredentials = {
    url: string;
    username: string;
    password: string;
    refresh_token?: string;
};

export type ImisAccessContext = {
    ".expires": string;
    ".issued": string;
    TenantId: string;
    access_token: string;
    "as:client_id": string;
    expires_in: number;
    refresh_token: string;
    token_type: string;
    userName: string;
};

export class ImisResponse {
    Properties: any = {};
}

export class ImisResponseItems<T = ImisResponse> {
    Count: string;
    HasNext: boolean;
    Limit: number;
    NextOffset: number;
    NextPageLink: string;
    Offset: number;
    TotalCount: number;
    Items: T[];
}

export class ImisResponseResult<T> {
    IsCachedResult: boolean;
    IsSuccessStatusCode: boolean;
    Message: string;
    ReasonPhrase: string;
    Result: T;
    StatusCode: number;
    ValidationResults: {
        Errors: any[];
        Warnings: any[];
    }
}

export class ImisFormWebhookResponse {
    result: boolean;
    message: string;

    constructor() {
        this.result = true;
        this.message = "";
    }
}

export type ImisRequestVerificationToken = {
    cookie: string;
    header: string;
}

export class PanelSourceMetadata {
    Description: string;
    EntityTypeName: string;
    Indexes: any[];
    PrimaryParentEntityTypeName: string;
    Properties: PanelSourceMetadataProperty[];
}

export class PanelSourceMetadataProperty {
    // GENERAL
    Name: string;
    Description: string;
    PropertyTypeName: string;
    Caption: string;
    DefaultValue: string & { $value: any };
    DisplayMask: string;
    RenderingInformation: any;
    IsReadOnly: boolean;
    Rule: any;
    Visible: boolean;
    // STRING
    MaxLength: number;
    // DATETIME
    DateTimeDataType: number;
    // DECIMAL
    Scale: number;
    Precision: number;
}

export class BOEntityDefinition {
    EntityTypeName: string;
    ObjectTypeName: string;
    PrimaryParentEntityTypeName: string;
    Description: string;
}

export class GenTableItem {
    Table_Name: string;
    Code: string;
    Description: string;
}

export class PanelDefinitionItem {
    PanelDefinitionId: string;
    Name: string;
    IsMultipleInstance: boolean;
    RelatedEntityName: string;
    SelectedEntityNames: string[];
    Description: string;
    Caption: string;
    Groups: {
        Columns: {
            Fields: {
                Caption: string;
                EntityName: string;
                HideInNormalView: boolean;
                IsReadOnly: boolean;
                IsRequired: boolean;
                LabelPosition: number;
                NumberOfLinesToDisplay: number;
                PropertyName: string;
            }[];
        }[];
    }[];
}

export class ChangeLogItem {
    // TODO: ActionType should be an enum for clarity
    ActionType: number;
    ChangeDate: string;
    ChangedByUserId: string;
    Changes: ChangeLogChange[];
}

export class ChangeLogChange {
    NewValue: string;
    Ordinal: number;
    OriginalValue: string;
    PropertyName: string;
}

export class ContentItem {
    Data: {
        Collapsed: boolean,
        Collapsible: boolean,
        ContentItemKey: string,
        ContentItemName: string,
        ContentKey: string,
        ContentTypeKey: string,
        CssClass: string,
        DisplayOnExtraSmallScreens: boolean,
        DisplayOnLargeScreens: boolean,
        DisplayOnMediumScreens: boolean,
        DisplayOnSmallScreens: boolean,
        DoNotRenderInDesignMode: boolean,
        IconUrl: string,
        LayoutZone: number,
        LicenseKeyRestriction: boolean,
        ModuleSpecificSetting: boolean,
        PartTitle: string,
        PartTitleHeadingLevel: number,
        Settings: { [key: string]: any },
        ShowBorder: boolean,
        ShowTitleFlag: boolean,
        SortOrder: number
    }
}

export class DocumentItem {
    AlternateName: string;
    Description: string;
    DocumentId: string;
    DocumentTypeId: string;
    DocumentVersionId: string;
    Name: string;
    Path: string;
    Status: string;
    Data: any;
}

export class QueryDefinitionItem {
    Document: DocumentItem;
    Parameters: any[];
    Path: string;
    Properties: {
        Alias: string;
        Caption: string;
        DataTypeName: string;
        DisplayFormat: string;
        DisplayOrder: number;
        Link: string;
        Name: string;
        PropertyName: string;
        QuerySourceId: string;
    }[];
    QueryDefinitionId: string;
    Relations: {
        LeftPropertyName: string;
        LeftQuerySourceId: string;
        RelationType: string;
        RightPropertyName: string;
        RightQuerySourceId: string;
    }[];
    Sources: {
        BusinessControllerName: string;
        Description: string;
        Name: string;
        QuerySourceId: string;
        QuerySourceType: number;
    }[];
}

export class FormItem {
    FormDesignerLibraryId: number;
    FormName: string;
    FormDescription: string;
    FormHtmlCode: string;
    FormStatus: string;
    FormVersion: number;
    LastSubmission: string;
    FormMessages: string;
    LastHealthCheck: string;
    FormRules: string;
    FormRulesScript: string;
    FormDesignerTypeCode: string;
    FormTypeCode: number;
    FormVersionKey: string;
}
